import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LogIn, LogOut as LogOutIcon, Coffee, Play, Clock, CheckCircle2, AlertCircle, ScanLine, MapPin, ShieldCheck, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import QRScanner from '@/components/time/QRScanner';

const BREAK_ALERT_MINUTES = 21;

const SecureTimeclock = ({ auth }) => {
  const [pendingAction, setPendingAction] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [todayEntry, setTodayEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [gpsStatus, setGpsStatus] = useState('pending');
  const [breakElapsed, setBreakElapsed] = useState(0);
  const [lastPunchMsg, setLastPunchMsg] = useState('');
  const alertIntervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const loadToday = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/time/employee/${auth.employee.id}/today`);
      setTodayEntry(res.data);
    } catch {}
  }, [auth.employee.id]);

  useEffect(() => { loadToday(); }, [loadToday]);

  // Break timer + alert
  useEffect(() => {
    if (!todayEntry) return;
    const breaks = todayEntry.breaks || [];
    const activeBreak = breaks.length > 0 && !breaks[breaks.length - 1].end_time ? breaks[breaks.length - 1] : null;

    if (!activeBreak) {
      setBreakElapsed(0);
      if (alertIntervalRef.current) { clearInterval(alertIntervalRef.current); alertIntervalRef.current = null; }
      return;
    }

    const timer = setInterval(() => {
      const start = new Date(activeBreak.start_time);
      const elapsed = (Date.now() - start.getTime()) / 60000;
      setBreakElapsed(elapsed);
      if (elapsed >= BREAK_ALERT_MINUTES && !alertIntervalRef.current) {
        alertIntervalRef.current = setInterval(() => playAlarmSound(), 5000);
        playAlarmSound();
      }
    }, 1000);

    return () => {
      clearInterval(timer);
      if (alertIntervalRef.current) { clearInterval(alertIntervalRef.current); alertIntervalRef.current = null; }
    };
  }, [todayEntry]);

  const playAlarmSound = () => {
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination); gain.gain.value = 0.8;
      const now = ctx.currentTime;
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.setValueAtTime(1100, now + 0.15);
      osc.frequency.setValueAtTime(880, now + 0.3);
      osc.frequency.setValueAtTime(1100, now + 0.45);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.7);
      osc.start(now); osc.stop(now + 0.7);
    } catch {}
  };

  const getGPS = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('GPS non supporte')); return; }
    setGpsStatus('loading');
    navigator.geolocation.getCurrentPosition(
      pos => { setGpsStatus('ok'); resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }); },
      () => { setGpsStatus('error'); reject(new Error('Activez la localisation GPS sur votre telephone')); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });

  // Step 1: User clicks an action button → open scanner
  const initiateAction = (actionType) => {
    setPendingAction(actionType);
    setShowScanner(true);
  };

  // Step 2: QR scanned → validate + GPS + punch
  const handleQRScan = async (scannedText) => {
    setShowScanner(false);
    const text = (scannedText || '').trim();
    let token = '';
    if (text.startsWith('VORTEKIO-PUNCH:')) token = text.split('VORTEKIO-PUNCH:')[1];
    else if (text.length >= 10 && text.length <= 20) token = text;

    if (!token) { toast.error('QR code non reconnu'); setPendingAction(null); return; }

    setLoading(true);
    try {
      // Validate token
      await axios.post(`${API}/timeclock/validate-token`, { token });

      // Get GPS
      let gps;
      try { gps = await getGPS(); } catch (err) { toast.error(err.message); setLoading(false); setPendingAction(null); return; }

      // Punch
      const res = await axios.post(`${API}/timeclock/punch`, {
        token,
        employee_id: auth.employee.id,
        action_type: pendingAction,
        latitude: gps.latitude,
        longitude: gps.longitude,
      });

      toast.success(res.data.message);
      setLastPunchMsg(res.data.message);

      if (pendingAction === 'break_end' && alertIntervalRef.current) {
        clearInterval(alertIntervalRef.current); alertIntervalRef.current = null;
      }

      await loadToday();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de pointage');
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  };

  const getStatus = () => {
    if (!todayEntry || !todayEntry.clock_in) return 'absent';
    if (todayEntry.clock_out) return 'completed';
    const breaks = todayEntry.breaks || [];
    if (breaks.length > 0 && !breaks[breaks.length - 1].end_time) return 'on_break';
    return 'active';
  };

  const status = getStatus();
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '--:--';

  const actionLabels = {
    clock_in: 'ENTREE',
    clock_out: 'SORTIE',
    break_start: 'PAUSE',
    break_end: 'REPRENDRE',
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <div className="sticky top-0 z-10 border-b border-white/10 px-4 py-3 flex items-center gap-3" style={{ background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(12px)' }}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/employee/dashboard')} className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="back-button">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-white font-semibold">Timbrage Securise</h1>
        <div className="flex-1" />
        <div className="flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-emerald-500" />
          <span className="text-xs text-emerald-400">QR + GPS</span>
        </div>
      </div>

      <div className="flex-1 p-4 max-w-md mx-auto w-full space-y-5">
        {/* Clock */}
        <div className="text-center py-3">
          <div className="text-5xl font-mono font-bold text-white tabular-nums tracking-tight" data-testid="current-time">
            {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
        </div>

        {/* Status card */}
        <Card className="p-5 rounded-2xl text-center space-y-4 bg-slate-800 border-slate-700" data-testid="status-card">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
            status === 'active' ? 'bg-emerald-900/50 text-emerald-400' :
            status === 'on_break' ? (breakElapsed >= BREAK_ALERT_MINUTES ? 'bg-red-900/50 text-red-400 animate-pulse' : 'bg-amber-900/50 text-amber-400') :
            status === 'completed' ? 'bg-blue-900/50 text-blue-400' : 'bg-slate-700 text-slate-300'
          }`} data-testid="tc-status">
            {status === 'active' && <CheckCircle2 className="w-4 h-4" />}
            {status === 'on_break' && <Coffee className="w-4 h-4" />}
            {status === 'completed' && <Clock className="w-4 h-4" />}
            {status === 'absent' && <AlertCircle className="w-4 h-4" />}
            {status === 'active' ? 'En service' : status === 'on_break' ? 'En pause' : status === 'completed' ? 'Journee terminee' : 'Non pointe'}
          </div>

          {/* Break timer */}
          {status === 'on_break' && (
            <div className={`p-3 rounded-xl ${breakElapsed >= BREAK_ALERT_MINUTES ? 'bg-red-900/40 border border-red-500/50 animate-pulse' : 'bg-amber-900/20 border border-amber-500/30'}`} data-testid="break-timer">
              <p className="text-xs text-slate-400 mb-1">Duree de la pause</p>
              <p className={`text-3xl font-mono font-bold ${breakElapsed >= BREAK_ALERT_MINUTES ? 'text-red-400' : 'text-amber-400'}`}>
                {Math.floor(breakElapsed)}:{String(Math.floor((breakElapsed % 1) * 60)).padStart(2, '0')}
              </p>
              {breakElapsed >= BREAK_ALERT_MINUTES && (
                <div className="flex items-center justify-center gap-2 mt-2">
                  <Volume2 className="w-4 h-4 text-red-400 animate-bounce" />
                  <p className="text-red-400 text-xs font-bold">PAUSE DEPASSEE ! Reprenez le travail</p>
                </div>
              )}
              <p className="text-xs text-slate-500 mt-1">Max: 20 min</p>
            </div>
          )}

          {/* Today summary */}
          {todayEntry && todayEntry.clock_in && (
            <div className="flex justify-center gap-6 text-sm">
              <div><span className="text-slate-400">Entree</span><p className="font-bold font-mono text-lg text-emerald-400" data-testid="tc-clock-in">{fmtTime(todayEntry.clock_in)}</p></div>
              {todayEntry.clock_out && (<div><span className="text-slate-400">Sortie</span><p className="font-bold font-mono text-lg text-red-400" data-testid="tc-clock-out">{fmtTime(todayEntry.clock_out)}</p></div>)}
              {todayEntry.total_hours != null && (<div><span className="text-slate-400">Total</span><p className="font-bold font-mono text-lg text-white" data-testid="tc-total">{todayEntry.total_hours.toFixed(1)}h</p></div>)}
            </div>
          )}

          {todayEntry?.breaks?.length > 0 && (
            <div className="text-xs text-slate-400 space-y-1">
              {todayEntry.breaks.map((b, i) => (
                <div key={i} className="flex justify-between px-4">
                  <span>Pause {i + 1}</span>
                  <span className="font-mono">{fmtTime(b.start_time)} - {b.end_time ? fmtTime(b.end_time) : 'en cours'}</span>
                  <span>{b.duration_minutes != null ? `${b.duration_minutes} min` : '...'}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* INSTRUCTION: Each action requires scanning the QR code */}
        <div className="bg-slate-800/50 rounded-xl p-3 text-center">
          <p className="text-xs text-slate-400">
            <ScanLine className="w-3 h-3 inline mr-1" />
            Chaque action necessite de scanner le QR code sur l'ecran du poste
          </p>
        </div>

        {/* Action buttons — each one opens the scanner */}
        <div className="space-y-3" data-testid="action-buttons">
          {(status === 'absent' || status === 'completed') && (
            <Button onClick={() => initiateAction('clock_in')} disabled={loading}
              className="w-full h-16 rounded-2xl text-lg font-bold bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="clock-in-btn">
              <ScanLine className="w-6 h-6 mr-3" />
              {loading && pendingAction === 'clock_in' ? 'Verification...' : (status === 'completed' ? 'RE-ENTREE (scanner QR)' : 'ENTREE (scanner QR)')}
            </Button>
          )}

          {status === 'active' && (
            <>
              <Button onClick={() => initiateAction('clock_out')} disabled={loading}
                className="w-full h-16 rounded-2xl text-lg font-bold bg-red-600 hover:bg-red-700 text-white" data-testid="clock-out-btn">
                <ScanLine className="w-6 h-6 mr-3" />
                {loading && pendingAction === 'clock_out' ? 'Verification...' : 'SORTIE (scanner QR)'}
              </Button>
              <Button onClick={() => initiateAction('break_start')} disabled={loading} variant="outline"
                className="w-full h-14 rounded-2xl text-base font-semibold border-amber-400/30 text-amber-400 hover:bg-amber-500/10" data-testid="break-start-btn">
                <ScanLine className="w-5 h-5 mr-2" />
                {loading && pendingAction === 'break_start' ? 'Verification...' : 'PAUSE (scanner QR)'}
              </Button>
            </>
          )}

          {status === 'on_break' && (
            <Button onClick={() => initiateAction('break_end')} disabled={loading}
              className={`w-full h-16 rounded-2xl text-lg font-bold text-white ${breakElapsed >= BREAK_ALERT_MINUTES ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-testid="break-end-btn">
              <ScanLine className="w-6 h-6 mr-3" />
              {loading && pendingAction === 'break_end' ? 'Verification...' : 'REPRENDRE (scanner QR)'}
            </Button>
          )}
        </div>

        {/* Last punch message */}
        {lastPunchMsg && (
          <div className="text-center">
            <p className="text-xs text-emerald-400">{lastPunchMsg}</p>
          </div>
        )}

        {/* GPS indicator */}
        <div className="flex items-center justify-center gap-2 text-xs">
          <MapPin className={`w-3 h-3 ${gpsStatus === 'ok' ? 'text-emerald-500' : gpsStatus === 'error' ? 'text-red-500' : 'text-slate-500'}`} />
          <span className={gpsStatus === 'ok' ? 'text-emerald-500' : gpsStatus === 'error' ? 'text-red-500' : 'text-slate-500'}>
            {gpsStatus === 'ok' ? 'GPS actif' : gpsStatus === 'error' ? 'GPS requis' : 'GPS en attente'}
          </span>
        </div>
      </div>

      {/* QR Scanner overlay */}
      {showScanner && (
        <QRScanner
          onScan={handleQRScan}
          onClose={() => { setShowScanner(false); setPendingAction(null); }}
          actionType={pendingAction}
        />
      )}
    </div>
  );
};

export default SecureTimeclock;
